/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * IKONY NAWIGACJI — przeniesione z makiety 1:1
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Ścieżki są dosłownie te same co w `control-center-concept/js/shell.js`. To celowe: makieta jest
 * specyfikacją, a ikona narysowana „w tym duchu" to inna ikona.
 *
 * DLACZEGO WŁASNE, A NIE ZESTAW Z BIBLIOTEKI: te ikony są rysowane jedną kreską o stałej grubości
 * 1.8 i domykają się w kwadracie 24. Zestawy w rodzaju Material mieszają wypełnienie z konturem
 * i mają inną wagę optyczną — obok siebie w jednej szynie widać, że pochodzą z dwóch światów.
 *
 * DOSTĘPNOŚĆ: ikony są dekoracją przy tekście, nie zamiast niego. Stąd `aria-hidden`; nazwę
 * pozycji niesie etykieta obok, więc czytnik ekranu nie ma czego dublować.
 */

const D: Record<string, string[]> = {
  dzis: ['M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0', 'M12 7v5l3.5 2'],
  sprzedaz: ['M3 3v18h18', 'M7 15l4-5 3 2.5L20 7'],
  zamowienia: ['M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z', 'M8 8h8M8 12h8M8 16h5'],
  restauracje: ['M5 3v7a3 3 0 0 0 6 0V3', 'M8 10v11', 'M18 3c-1.6 1.6-2.5 3.7-2.5 6S17 21 17 21h2V3z'],
  szkoly: ['M3 9l9-5 9 5-9 5-9-5z', 'M7 11.5V17c0 1.7 2.2 3 5 3s5-1.3 5-3v-5.5'],
  dania: ['M12 13m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0', 'M6 6h12'],
  dostawy: [
    'M1 7.5A1.5 1.5 0 0 1 2.5 6h10A1.5 1.5 0 0 1 14 7.5V16H1z',
    'M14 9h4l3 3.5V16h-7z',
    'M5.5 18.5m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
    'M17.5 18.5m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0',
  ],
  insighty: ['M9 18h6', 'M10 21h4', 'M12 3a6 6 0 0 1 3.5 10.9c-.6.5-.9 1.2-.9 1.9H9.4c0-.7-.3-1.4-.9-1.9A6 6 0 0 1 12 3z'],
  powiazania: [
    'M6 6m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0',
    'M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0',
    'M8.5 8.5l7 7',
    'M18 3v4M16 5h4',
  ],
  jakosc: ['M12 3l8 3.5v5c0 5-3.4 8.6-8 9.5-4.6-.9-8-4.5-8-9.5v-5z', 'M9 12l2 2 4-4'],
  gotowosc: [
    'M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0',
    'M12 12m-4.5 0a4.5 4.5 0 1 0 9 0a4.5 4.5 0 1 0 -9 0',
    'M12 3v3M12 18v3M3 12h3M18 12h3',
  ],
  administracja: ['M12 3l7 4v5c0 4.5-3 8-7 9-4-1-7-4.5-7-9V7z', 'M12 9v4', 'M12 16h.01'],
  raporty: ['M6 3h8l4 4v14H6z', 'M14 3v4h4', 'M9 13h6M9 17h4'],
  system: ['M4 6h16M4 12h16M4 18h16', 'M8 6v12'],
};

/** Klucz ikony dla sekcji. Nieznany klucz dostaje ikonę przestrzeni, a nie pustkę. */
export function kluczIkony(idPrzestrzeni: string, idSekcji: string): string {
  // Klucze wzięte z `WORKSPACES` — sprawdzone, nie zgadnięte. Sekcja bez wpisu spada
  // na ikonę przestrzeni, więc dołożenie nowej nigdy nie daje pustego miejsca w szynie.
  const wprost: Record<string, string> = {
    'home:overview': 'dzis',
    'admin:users': 'administracja',
    'admin:schools': 'szkoly',
    'restaurants:sales': 'restauracje',
    'restaurants:dishes': 'dania',
    'restaurants:schools': 'szkoly',
    'restaurants:catalog': 'dania',
    'restaurants:menu': 'zamowienia',
    'crm:school-sales': 'sprzedaz',
    'crm:transactions': 'zamowienia',
    'crm:campaigns': 'insighty',
    'drive:deliveries': 'dostawy',
    'drive:schools': 'szkoly',
    'drive:history': 'zamowienia',
    'insights:all': 'insighty',
    'insights:coincidence': 'powiazania',
    'reports:reports': 'raporty',
    'system:status': 'system',
    'system:readiness': 'gotowosc',
    'system:quality': 'jakosc',
    'system:audit': 'zamowienia',
  };
  return (
    wprost[`${idPrzestrzeni}:${idSekcji}`] ??
    ({ home: 'dzis', crm: 'sprzedaz', restaurants: 'restauracje', drive: 'dostawy', insights: 'insighty', system: 'system', admin: 'administracja', reports: 'raporty' }[
      idPrzestrzeni
    ] ?? 'system')
  );
}

export function IkonaNawigacji({ klucz, rozmiar = 18 }: { klucz: string; rozmiar?: number }) {
  const sciezki = D[klucz] ?? D.system!;
  return (
    <svg
      viewBox="0 0 24 24"
      width={rozmiar}
      height={rozmiar}
      aria-hidden
      focusable="false"
      style={{ stroke: 'currentColor', fill: 'none', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', flex: 'none' }}
    >
      {sciezki.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
